import { useState, useEffect } from 'react';
import api from '../../api/axiosInstance';

export interface UseUnitPricesDataReturn {
  unitPrices: Record<string, any>;
  isUnitPricesLoaded: boolean;
  isRefreshingPrices: boolean;
  fixedVariables: { [key: string]: number };
  getAllUnitPricesData: () => Promise<any>;
  refreshUnitPricesData: (showAlert?: boolean) => Promise<any>;
  loadFixedVariables: () => Promise<any>;
}

export function useUnitPricesData(): UseUnitPricesDataReturn {
  const [unitPrices, setUnitPrices] = useState<Record<string, any>>({});
  const [isUnitPricesLoaded, setIsUnitPricesLoaded] = useState<boolean>(false);
  const [isRefreshingPrices, setIsRefreshingPrices] = useState<boolean>(false);
  const [lastChecksum, setLastChecksum] = useState<string | null>(null);
  const [fixedVariables, setFixedVariables] = useState<{ [key: string]: number }>({});

  const loadFixedVariables = async () => {
    try {
      const response = await api.get('/api/fixed-variables/latest');
      const variables = response.data;
      setFixedVariables(variables);
      return variables;
    } catch (error) {
      console.error('고정변수 데이터 로딩 중 오류:', error);
      const defaultVariables = {
        area_unit: 90000,
        vat_rate: 0.10,
        min_installationfee: 1500000,
        base_transportfee: 300000,
        wrapping_percent: 20.0,
      };
      setFixedVariables(defaultVariables);
      return defaultVariables;
    }
  };

  const getAllUnitPricesData = async () => {
    try {
      const timestamp = Date.now();
      const [pricesRes, checksumRes] = await Promise.all([
        api.get(`/api/unit-prices?_t=${timestamp}`),
        api.get(`/api/unit-prices/checksum?_t=${timestamp}`),
      ]);
      const unitPricesData = pricesRes.data;
      setUnitPrices(unitPricesData);
      setIsUnitPricesLoaded(true);
      if (checksumRes.data) {
        setLastChecksum(checksumRes.data.checksum);
      }
      return unitPricesData;
    } catch (error) {
      console.error('단가 데이터 로딩 중 오류:', error);
      throw error;
    }
  };

  const refreshUnitPricesData = async (showAlert = true) => {
    if (isRefreshingPrices) return;

    setIsRefreshingPrices(true);
    try {
      const timestamp = Date.now();
      const [pricesRes, checksumRes] = await Promise.all([
        api.get(`/api/unit-prices?_t=${timestamp}`),
        api.get(`/api/unit-prices/checksum?_t=${timestamp}`),
      ]);
      const unitPricesData = pricesRes.data;
      setUnitPrices(unitPricesData);

      if (checksumRes.data) {
        setLastChecksum(checksumRes.data.checksum);
      }

      if (showAlert) {
        alert('단가 데이터가 새로고침되었습니다.');
      }
      return unitPricesData;
    } catch (error) {
      console.error('단가 데이터 새로고침 중 오류:', error);
      if (showAlert) {
        alert('단가 데이터 새로고침 중 오류가 발생했습니다.');
      }
      throw error;
    } finally {
      setIsRefreshingPrices(false);
    }
  };

  const checkForDataChanges = async () => {
    try {
      const timestamp = Date.now();
      const res = await api.get(`/api/unit-prices/checksum?_t=${timestamp}`);
      const checksumData = res.data;
      if (lastChecksum && checksumData.checksum !== lastChecksum) {
        await refreshUnitPricesData(false);
      }
    } catch (error) {
      console.error('체크섬 확인 중 오류:', error);
    }
  };

  // 페이지 포커스 시 단가 데이터 자동 체크
  useEffect(() => {
    let lastFocusTime = Date.now();

    const handleFocus = async () => {
      if (Date.now() - lastFocusTime > 2000 && isUnitPricesLoaded) {
        try {
          await checkForDataChanges();
        } catch (error) {
          console.error('포커스 시 데이터 체크 실패:', error);
        }
      }
    };

    const handleBlur = () => {
      lastFocusTime = Date.now();
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnitPricesLoaded]);

  // 주기적으로 데이터 변경 체크 (5초마다)
  useEffect(() => {
    if (!isUnitPricesLoaded || !lastChecksum) return;

    const interval = setInterval(() => {
      checkForDataChanges();
    }, 5000);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUnitPricesLoaded, lastChecksum]);

  return {
    unitPrices,
    isUnitPricesLoaded,
    isRefreshingPrices,
    fixedVariables,
    getAllUnitPricesData,
    refreshUnitPricesData,
    loadFixedVariables,
  };
}
